import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MetaTag, SeoData } from '../models/seo-data.model';
import { LinkService } from './link.service';
import { ImageComponent } from '../models/image.model';
import { CmsMediaPrefixPipe } from '../pipes/cms-media-prefix.pipe';
import { environment } from 'environments/environment';
import { AlternateLinkService } from './alternate-link.service';
import { LanguageBilingualPaths } from '../helpers/models/base-page.model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { useStructuredDataHandler } from './use-structured-data-handler';
import { sanitizeInternalUrl, toAbsoluteSiteUrl } from '../utils/url-sanitizer.util';

@Injectable({ providedIn: 'root' })
export class SeoService {
  readonly #titleService = inject(Title);
  readonly #metaService = inject(Meta);
  readonly #linkService = inject(LinkService);
  readonly #alternateLinkService = inject(AlternateLinkService);
  readonly #translateService = inject(TranslateService);
  readonly #structuredDataHandler = useStructuredDataHandler()
  #medicalOrganizationSub?: Subscription;
  bilingualPaths?: LanguageBilingualPaths;
  contentType?: string;

  readonly tagsNames: string[] = [
    'name="description"',
    'type="application/ld+json"',
    'property="og:title"',
    'property="og:description"',
    'property="og:image"',
    'property="og:url"',
    'rel="canonical"',
    'name="robots"',
    'name="twitter:card"',
    'name="twitter:site"',
    'name="twitter:creator"',
    'property="fb:app_id"',
    'property="og:type"'
  ];

  removeTags() {
    this.#titleService.setTitle('');
    this.tagsNames.forEach((tag) => {
      this.#metaService.removeTag(tag);
      this.#linkService.removeTag(tag);
    });
  }

  setAlternateLinks(bilingualPaths?: LanguageBilingualPaths | undefined) {
    this.bilingualPaths = bilingualPaths;
    this.#alternateLinkService.clearAlternateLinks();

    const metaTagsMapping: Record<string, MetaTag> = {
      enUrl: { type: 'link', rel: 'alternate', hreflang: 'en-us', href: bilingualPaths?.englishPath ?? '' },
      heUrl: { type: 'link', rel: 'alternate', hreflang: 'he', href: bilingualPaths?.hebrewPath ?? '' },
      defaultUrl: { type: 'link', rel: 'alternate', hreflang: 'x-default', href: bilingualPaths?.hebrewPath ?? bilingualPaths?.englishPath ?? '' },
    };

    Object.keys(metaTagsMapping).forEach((key) => {
      const metaTag = metaTagsMapping[key];
      if (metaTag.href) {
        const href = this.removeHomeFromUrl(metaTag.href);
        this.#alternateLinkService.addAlternateTag({ rel: metaTag.rel ?? '', hreflang: metaTag.hreflang,  href });
      }
    });
  }

  setTags(seoData: SeoData, image: ImageComponent | undefined, aliasOGType: string) {
    this.removeTags();
    const pipe = new CmsMediaPrefixPipe();

    const metaTagsMapping: Record<string, MetaTag> = {
      title: { type: 'title', value: seoData.title },
      metaDescription: { type: 'name', name: 'description', value: seoData.metaDescription },
      openGraphTitle: { type: 'property', name: 'og:title', value: seoData.openGraphTitle },
      openGraphDescription: { type: 'property', name: 'og:description', value: seoData.openGraphDescription },
      openGraphImage: { type: 'property', name: 'og:image', value: image?.image?.url? pipe.transform(image?.image?.url) :  `${environment.clientUrl}/assets/images/79db837e261ced2bed50c4663a9366a9.png`},

      robots: { type: 'name', name: 'robots', value: seoData.robots.join(',') },
      twitterCardType: { type: 'name', name: 'twitter:card', value: seoData.twitterCardType },
      twitterSite: { type: 'name', name: 'twitter:site', value: seoData.twitterSite },
      twitterCreator: { type: 'name', name: 'twitter:creator', value: seoData.twitterCreator },
      facebookId: { type: 'property', name: 'fb:app_id', value: seoData.facebookId },
      type: {type: 'property', name: 'og:type', value: aliasOGType }
    };

    Object.keys(metaTagsMapping).forEach((key) => {
      const metaTag = metaTagsMapping[key];
      if (metaTag.value || metaTag.href) {
        if (metaTag.type === 'title') {
          this.#titleService.setTitle(metaTag.value ?? '');
        } else if (metaTag.type === 'link') {
          this.#linkService.addTag({ rel: metaTag.rel ?? '', href: metaTag.href ?? '' });
        } else {
          this.#metaService.addTag({ [metaTag.type]: metaTag.name ?? '', content: metaTag.value ?? '' });
        }
      }
    });
  }

  private getCanonicalUrl(seoData?: SeoData): string {
    const providedUrl = seoData?.canonicalUrl;
    const baseUrl = providedUrl || window.location.href.split('?')[0].split('#')[0];
    return this.removeHomeFromUrl(baseUrl);
  }

  private removeHomeFromUrl(url: string): string {
    if (!url) return url;
    let cleanUrl = url.replace(/\/he(?=\/|$)/, '');

    cleanUrl = cleanUrl.replace(/\/home\/?$/, '');
    cleanUrl = cleanUrl.replace(/\/$/, '');
    
    let isRootPath = false;
    try {
      const urlObj = new URL(cleanUrl);
      isRootPath = urlObj.pathname === '' || urlObj.pathname === '/';
    } catch {
      isRootPath = cleanUrl === '' || cleanUrl === '/';
    }
    
    if (isRootPath && !cleanUrl.endsWith('/')) {
      cleanUrl += '/';
    }
    
    return cleanUrl;
  }

  ensureCanonicalTag(seoData?: SeoData) {
    this.#linkService.removeTag('rel="canonical"');
    
    const canonicalUrl = this.getCanonicalUrl(seoData);
    this.#linkService.addTag({ rel: 'canonical', href: canonicalUrl });
  }

  setSchema(key: string, schema: Record<string, any>) {
    try {
      this.#structuredDataHandler.set(key, schema);
    } catch (error) {
      console.error('Failed to set schema:', error);
    }
  }

  deleteSchema(key: string) {
    try {
      this.#structuredDataHandler.delete(key);
    } catch (error) {
      console.error('Failed to delete schema:', error);
    }
  }

  // Breadcrumbs JSON-LD Markup
  setBreadcrumbSchema(
    breadcrumbs: {
      title: string;
      path?: string;
    }[]
  ) {
    try {
      // Delay to allow translations to load
      setTimeout(() => {
        const schema = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((breadcrumb, index) => {
            // Always attempt to translate the title since breadcrumbs may contain translation keys
            let name = breadcrumb.title || 'SHARED.BREADCRUMBS_HOME';
            const translated = this.#translateService.instant(name);

            // Use translated value if translation was found (returns different value)
            if (translated && translated !== name) {
              name = translated;
            }

            return {
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': toAbsoluteSiteUrl(sanitizeInternalUrl(breadcrumb.path)),
                name
              }
            };
          })
        };
        this.#structuredDataHandler.set('BreadcrumbList', schema);
      }, 100);
    } catch (error) {
      console.error('Failed to set breadcrumb schema:', error);
    }
  }

  deleteBreadcrumbSchema() {
    this.deleteSchema('BreadcrumbList');
  }

  // Medical Organization JSON-LD Markup for Home Page
  setMedicalOrganizationSchema() {
    this.#medicalOrganizationSub?.unsubscribe();
    this.#medicalOrganizationSub = this.#translateService
      .stream(['SEO.ORGANIZATION_NAME', 'SEO.SLOGAN'])
      .subscribe({
        next: (t) => {
          this.#structuredDataHandler.set('MedicalOrganization', {
            '@context': 'https://schema.org',
            '@type': 'MedicalOrganization',
            name: t['SEO.ORGANIZATION_NAME'],
            url: environment.clientUrl,
            logo: 'https://www.sheba.co.il/cms-media/media/0mfdkobr/logo-main-heb-web-30.svg',
            slogan: t['SEO.SLOGAN'],
            sameAs: [
              'https://www.facebook.com/SHEBA.MEDICAL?ref=ts',
              'https://x.com/SHEBA_',
              'https://www.instagram.com/sheba.medical/',
              'https://www.youtube.com/@shebamedicalcenter6539',
              'https://www.linkedin.com/company/sheba-tel-hashomer-city-of-health/'
            ]
          });
        },
        error: (error) => {
          console.error('Failed to set medical organization schema:', error);
        }
      });
  }

  deleteMedicalOrganizationSchema() {
    this.#medicalOrganizationSub?.unsubscribe();
    this.#medicalOrganizationSub = undefined;
    this.deleteSchema('MedicalOrganization');
  }
  setContentType(contentType: string) {
   this.contentType = contentType;
  }

  setNoIndexFollow() {
    this.#metaService.updateTag({ name: 'robots', content: 'noindex, follow' });
  }
}
